import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const DISCOVERY_DOCS = ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'];
const SCOPES = 'https://www.googleapis.com/auth/drive.readonly';

export const useGoogleDrive = (googleClientId) => {
  const [driveConnected, setDriveConnected] = useState(false);
  const [driveFiles, setDriveFiles] = useState([]);
  const [isLoadingDrive, setIsLoadingDrive] = useState(false);
  const [gapiInitialized, setGapiInitialized] = useState(false);

  const initGoogleClient = useCallback(async () => {
    try {
      if (!window.gapi?.client) {
        console.log('GAPI client not available');
        return;
      }
      
      await window.gapi.client.init({
        clientId: googleClientId,
        discoveryDocs: DISCOVERY_DOCS,
        scope: SCOPES
      });

      setGapiInitialized(true);

      // Check if already signed in
      const authInstance = window.gapi.auth2?.getAuthInstance();
      if (authInstance) {
        const isSignedIn = authInstance.isSignedIn.get();
        setDriveConnected(isSignedIn);

        // Listen for sign-in state changes
        authInstance.isSignedIn.listen((signedIn) => {
          setDriveConnected(signedIn);
        });
      }

      console.log('Google Drive API initialized');
    } catch (error) {
      // Handle the error object properly - Google API throws an object, not an Error
      const errorDetails = error?.details || error?.error || 'Unknown error';
      console.log('Google Drive initialization skipped:', errorDetails);
      // Don't throw or show toast - this is expected when Client ID isn't configured for this origin
      setGapiInitialized(false);
    }
  }, [googleClientId]);

  const initGoogleDrive = useCallback(() => {
    if (window.gapi?.load) {
      try {
        window.gapi.load('client:auth2', {
          callback: initGoogleClient,
          onerror: () => {
            console.log('Google API load error - skipping Google Drive integration');
            setGapiInitialized(false);
          }
        });
      } catch (e) {
        console.log('Google API load failed:', e);
        setGapiInitialized(false);
      }
    }
  }, [initGoogleClient]);

  useEffect(() => {
    if (googleClientId && typeof window !== 'undefined' && window.gapi) {
      initGoogleDrive();
    }
  }, [googleClientId, initGoogleDrive]);

  const connectGoogleDrive = async () => {
    if (!gapiInitialized) {
      toast.error('Google Drive not initialized. Please configure Client ID in Settings.');
      return;
    }

    try {
      const authInstance = window.gapi?.auth2?.getAuthInstance();
      if (!authInstance) {
        toast.error('Google Drive authentication not available');
        return;
      }
      await authInstance.signIn();
      setDriveConnected(true);
      toast.success('Connected to Google Drive!');
      await loadDriveFiles();
    } catch (error) {
      console.error('Sign-in failed:', error);
      toast.error('Failed to connect to Google Drive');
    }
  };

  const disconnectGoogleDrive = async () => {
    if (!gapiInitialized) return;

    try {
      const authInstance = window.gapi?.auth2?.getAuthInstance();
      if (!authInstance) return;
      await authInstance.signOut();
      setDriveConnected(false);
      setDriveFiles([]);
      toast.success('Disconnected from Google Drive');
    } catch (error) {
      console.error('Sign-out failed:', error);
      toast.error('Failed to disconnect');
    }
  };

  const loadDriveFiles = async (searchQuery = '') => {
    if (!driveConnected) {
      toast.error('Please connect to Google Drive first');
      return;
    }

    setIsLoadingDrive(true);

    try {
      let query = "mimeType='application/vnd.google-apps.document' or mimeType='text/plain'";
      if (searchQuery) {
        query += ` and name contains '${searchQuery}'`;
      }

      if (!window.gapi?.client?.drive?.files) {
        throw new Error('Google Drive API not available');
      }

      const response = await window.gapi.client.drive.files.list({
        pageSize: 20,
        fields: 'files(id, name, mimeType, modifiedTime)',
        q: query,
        orderBy: 'modifiedTime desc'
      });

      const files = response?.result?.files || [];
      setDriveFiles(files);
    } catch (error) {
      console.error('Failed to load files:', error);
      toast.error('Failed to load files from Google Drive');
    } finally {
      setIsLoadingDrive(false);
    }
  };

  const loadFromGoogleDoc = async (fileId, fileName) => {
    try {
      setIsLoadingDrive(true);
      
      if (!window.gapi?.client?.drive?.files) {
        throw new Error('Google Drive API not available');
      }

      // Export Google Doc as plain text
      const response = await window.gapi.client.drive.files.export({
        fileId: fileId,
        mimeType: 'text/plain'
      });

      const content = response?.body || '';
      
      // Remove file extension from title
      const title = fileName.replace(/\.(docx?|txt)$/i, '');

      setIsLoadingDrive(false);
      toast.success('Document loaded from Google Drive!');

      return { content, title };
    } catch (error) {
      console.error('Failed to load document:', error);
      setIsLoadingDrive(false);
      toast.error('Failed to load document content');
      return null;
    }
  };

  return {
    driveConnected,
    driveFiles,
    isLoadingDrive,
    connectGoogleDrive,
    disconnectGoogleDrive,
    loadDriveFiles,
    loadFromGoogleDoc,
    gapiInitialized
  };
};
